import React, { ReactElement, useRef } from 'react'
import styled from '@emotion/styled'
import { motion } from 'motion/react'
import { TRecipeClaim } from '@mindfulchefuk/features/Recipes/interfaces'
import { theme, Box } from '@mindfulchefuk/design-system'

const popupInitial = {
  y: 0,
  opacity: 0,
  scale: 0.7,
  display: 'none',
}

const popupVariants = {
  active: {
    y: -8,
    opacity: 1,
    scale: 1,
    display: 'block',
  },
}

const AnimatedDiv = styled(motion.div)`
  display: flex;
  tabindex: 0;
  align-items: center;
  cursor: pointer;
`

const AnimatedText = styled(motion.p)<{
  backgroundColor: string
  textColor: string
}>`
  ${theme.typography['body-md-500']};
  padding: ${theme.space[6]} ${theme.space[12]};
  bottom: ${theme.space[48]};
  width: max-content;
  border-radius: ${theme.space[12]};
  box-shadow: ${theme.shadows.standard};
  margin-bottom: ${theme.space[4]};
  position: absolute;
  max-width: ${theme.space[160]};
  background: ${(props) => props.backgroundColor};
  color: ${(props) => props.textColor};
`

const Lozenge = styled(motion.div)<
  Pick<TRecipeClaim, 'textColor' | 'backgroundColor'>
>`
  background-color: ${(props) => `${props.backgroundColor}`};
  color: ${(props) => props.textColor || '#FFFFFF'};
  width: max-content;
  text-align: center;
  padding: ${theme.space[4]} ${theme.space[12]};
  border-radius: ${theme.space[20]};
  margin-right: ${theme.space[14]};
  margin-bottom: ${theme.space[14]};
  font-size: ${theme.typography['body-sm-500-caps']};
`

export const RecipeModalClaimLozenge = ({
  backgroundColor,
  description,
  name,
  textColor,
}: TRecipeClaim): ReactElement => {
  const ref = useRef(null)

  const hasDefaultColors =
    backgroundColor === '#FFFFFF' || backgroundColor === '#ffffff'

  const backgroundColorOrFallback = hasDefaultColors
    ? theme.colors['pepper-50']
    : backgroundColor

  const textColorOrFallback = hasDefaultColors ? theme.colors.black : textColor

  // This re-enables focusing/blurring the roundel on click on touch devices
  // We need to disable focusing on desktop as it doesn't make sense when popup is already visible from hover
  // It's still possible to hover and focus with the keyboard but this is a rarer use case (and expected IMO)
  const handleTouchEnd = () => {
    const isActive = document.activeElement === ref.current
    if (isActive) {
      ref.current.blur()
    } else {
      ref.current.focus()
    }
    return false
  }

  return (
    <Box position="relative">
      <AnimatedDiv
        ref={ref}
        onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
          e.stopPropagation()
        } // This stops the RecipeModal from being opened
        onMouseDown={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
          e.preventDefault()
        } // This stops the roundels on desktop being focused
        onTouchEnd={handleTouchEnd}
        whileHover="active"
        whileFocus="active"
      >
        {description && (
          <AnimatedText
            backgroundColor={backgroundColorOrFallback}
            data-testid="modal-claim-lozenge-popup"
            initial={popupInitial}
            onTouchEnd={(
              e: React.MouseEvent<HTMLParagraphElement, MouseEvent>
            ) => e.stopPropagation()} // This allows you to tap inside the popup
            textColor={textColor}
            variants={popupVariants}
          >
            {description}
          </AnimatedText>
        )}
        <Lozenge
          aria-label={`${name}: ${description}`}
          backgroundColor={backgroundColorOrFallback}
          textColor={textColorOrFallback}
        >
          {name.toUpperCase()}
        </Lozenge>
      </AnimatedDiv>
    </Box>
  )
}
